import { useMemo } from "react";
import useAppSelector from "@/core/helpers/useAppSelector";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import { IS_DEV } from "@/domains/main/constants/other";
import { inventoryMapSelector } from "@/domains/inventory/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import { sortInventories } from "@/domains/inventory/helpers/sortedInventories";
import InventoryAddItem from "@/domains/inventory/containers/InventoryAddItem";
import InventoryDndContextWrapperContainer from "@/domains/inventory/containers/InventoryDndContextWrapperContainer";
import InventoryCharacterBagContainer from "@/domains/inventory/containers/character/InventoryCharacterBagContainer";
import InventoryCharacterContainer from "@/domains/inventory/containers/character/InventoryCharacterContainer";
import InventoryCharacterDropSpaceContainer from "@/domains/inventory/containers/character/InventoryCharacterDropSpaceContainer";
import InventoryCharacterEnvironmentContainer from "@/domains/inventory/containers/character/InventoryCharacterEnvironmentContainer";
import InventoryCharacterGarbageContainer from "@/domains/inventory/containers/character/InventoryCharacterGarbageContainer";
import InventoryCharacterGloveBoxContainer from "@/domains/inventory/containers/character/InventoryCharacterGloveBoxContainer";
import InventoryCharacterSafeContainer from "@/domains/inventory/containers/character/InventoryCharacterSafeContainer";
import InventoryCharacterTrunkContainer from "@/domains/inventory/containers/character/InventoryCharacterTrunkContainer";

type Props = {
  isOpen: boolean;
};

export default function CharacterInventoryContainer({ isOpen }: Props) {
  const playerInfo = useAppSelector(playerInfoSelector);
  const inventoryMap = useAppSelector(inventoryMapSelector);

  const sortedInventories = useMemo(() => sortInventories(inventoryMap), [inventoryMap]);

  let hasOpenSafe = false;

  if (!isOpen || !playerInfo) return null;

  return (
    <InventoryDndContextWrapperContainer>
      <InventoryCharacterDropSpaceContainer>
        {IS_DEV && <InventoryAddItem />}
        <InventoryAddItem />
        {sortedInventories.map(([key, inventory]) => {
          switch (inventory.ownerType) {
            case OwnerType.Bag:
              return <InventoryCharacterBagContainer key={key} inventoryId={inventory.id} />;
            case OwnerType.Environment:
              return (
                <InventoryCharacterEnvironmentContainer key={key} inventoryId={inventory.id} />
              );
            case OwnerType.VehicleTrunk:
              return <InventoryCharacterTrunkContainer key={key} inventoryId={inventory.id} />;
            case OwnerType.Character:
              return <InventoryCharacterContainer key={key} inventoryId={inventory.id} />;
            case OwnerType.EstateSafe: {
              if (hasOpenSafe) {
                return null;
              } else {
                hasOpenSafe = true;
                return <InventoryCharacterSafeContainer key={key} inventoryId={inventory.id} />;
              }
            }
            case OwnerType.GarbageContainer:
              return <InventoryCharacterGarbageContainer key={key} inventoryId={inventory.id} />;
            case OwnerType.VehicleGloveBox:
              return <InventoryCharacterGloveBoxContainer key={key} inventoryId={inventory.id} />;
            default:
              return null;
          }
        })}
      </InventoryCharacterDropSpaceContainer>
    </InventoryDndContextWrapperContainer>
  );
}
